import {useMemo, useState} from "react";
import {CheckIcon, ChevronRightIcon, ClipboardDocumentIcon} from "@heroicons/react/20/solid";
import {OtsBadge} from "../components/OtsBadge";
import {OtsActionButton} from "../components/OtsButton";
import type {ObjectRef} from "../diagnostics/types";
import type {DecisionGraphModel, DecisionGraphNode} from "./decision-graph-model";
import {DecisionStepBlock} from "./DecisionStepBlock";
import {decisionStepsToText, orderDecisionSteps} from "./decision-steps-text";

export interface DecisionStepsProps {
  model: DecisionGraphModel;
  label?: string;
  /** Marks the step the viewer came from and opens it; the others with nothing wrong start collapsed. */
  highlight?: (node: DecisionGraphNode) => boolean;
  highlightLabel?: string;
  /** Results lead the list. */
  outputsFirst?: boolean;
  /** First line of the copied text. */
  copyTitle?: string;
  canNavigate?: (ref: ObjectRef) => boolean;
  onNavigate?: (ref: ObjectRef) => void;
}

const initiallyOpen = (nodes: DecisionGraphNode[], highlight?: (node: DecisionGraphNode) => boolean) => new Set(nodes
  .filter(node => node.kind === "output" || node.status === "rejected" || (highlight ? highlight(node) : node.kind !== "input"))
  .map(node => node.id));

/**
 * A recorded calculation read top to bottom, like a worksheet: each step is a card holding its headed blocks.
 * Use it for linear traces; {@link DecisionGraph} is for flows that branch.
 */
export const DecisionSteps = ({
  model, label = "Calculation steps", highlight, highlightLabel = "Current", outputsFirst = true, copyTitle, canNavigate, onNavigate,
}: DecisionStepsProps) => {
  const nodes = useMemo(() => orderDecisionSteps(model, outputsFirst), [model, outputsFirst]);
  const [open, setOpen] = useState(() => initiallyOpen(nodes, highlight));
  const [copied, setCopied] = useState(false);
  const allOpen = nodes.every(node => open.has(node.id));

  const toggle = (id: string) => setOpen(previous => {
    const next = new Set(previous);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });

  const copy = async () => {
    if (typeof navigator === "undefined" || !navigator.clipboard) return;
    await navigator.clipboard.writeText(decisionStepsToText(model, {title: copyTitle, outputsFirst}));
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1200);
  };

  return <section aria-label={label} className="space-y-3">
    <div className="flex flex-wrap items-center justify-end gap-2">
      <OtsActionButton size="compact" variant="text" onClick={() => setOpen(allOpen ? new Set() : new Set(nodes.map(node => node.id)))}>
        {allOpen ? "Collapse all" : "Expand all"}
      </OtsActionButton>
      <OtsActionButton size="compact" variant="secondary" icon={copied ? <CheckIcon/> : <ClipboardDocumentIcon/>} onClick={() => void copy()}>
        {copied ? "Copied" : "Copy as text"}
      </OtsActionButton>
    </div>
    <ol className="space-y-2">
      {nodes.map(node => {
        const expanded = open.has(node.id);
        const highlighted = highlight?.(node) ?? false;
        const rejected = node.status === "rejected";
        const summary = node.lines[0];
        const bodyId = `decision-step-${node.id}`;
        return <li key={node.id} className={[
          "rounded-xl border bg-[var(--ots-surface)]",
          rejected ? "border-[var(--ots-danger-outline)]" : highlighted ? "border-[var(--ots-primary)]" : "border-[var(--ots-outline-variant)]",
        ].join(" ")}>
          <button type="button" aria-expanded={expanded} aria-controls={bodyId} onClick={() => toggle(node.id)}
            className="flex w-full min-w-0 items-start gap-2 rounded-xl px-3 py-2.5 text-left hover:bg-[var(--ots-surface-variant)]">
            <ChevronRightIcon aria-hidden="true" className={["mt-0.5 size-4 shrink-0 text-[var(--ots-text-muted)] transition-transform", expanded ? "rotate-90" : ""].join(" ")}/>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold text-[var(--ots-text)]">{node.title}</span>
                {node.kind === "output" && <OtsBadge size="compact" tone="success">Result</OtsBadge>}
                {rejected && <OtsBadge size="compact" tone="danger">Error</OtsBadge>}
                {highlighted && <OtsBadge size="compact" tone="info">{highlightLabel}</OtsBadge>}
              </span>
              {summary && !expanded && <span className="mt-0.5 block truncate text-xs text-[var(--ots-text-muted)]">{summary}</span>}
            </span>
          </button>
          {expanded && <div id={bodyId} className="space-y-3 border-t border-[var(--ots-outline-variant)] px-3 py-3">
            {node.messages?.length
              ? node.messages.map((message, index) => <DecisionStepBlock key={index} message={message} canNavigate={canNavigate} onNavigate={onNavigate}/>)
              : node.lines.map((line, index) => <p key={index} className="text-sm">{line}</p>)}
          </div>}
        </li>;
      })}
    </ol>
  </section>;
};
